// What each waiting kind looks like in the Inbox's columns (#808).
//
// A `Record` over `InboxKind`, as `inboxView.ts::INBOX_KINDS` is: a kind added without a
// row here does not type-check. Each row says the kind's icon, its subject — the change's
// title, the resource's name — a line under the subject where the kind has one more fact
// worth a glance, and who asked. The workspace and the due time are the same for every
// kind and are the page's (`InboxPage.tsx`).
//
// A row reads an answered item as well as a waiting one: the answered list under each
// workspace uses the same columns, with the outcome in place of the due time.

import type { ReactNode } from "react";

import type { AnsweredItem, InboxItem, InboxKind } from "../api/inbox";
import { severityLabel } from "../api/automation";
import { CAPABILITY_TEXT, type Capability } from "../api/customers";
import { runPagePath } from "../runId";
import { INBOX_KINDS } from "./inboxView";

/** An item a row draws: waiting, or answered with its outcome. */
export type RowItem = InboxItem | AnsweredItem;
type RowOf<K extends InboxKind> = Extract<RowItem, { kind: K }>;

export interface KindRow<I> {
  /** A 24-unit path, in the rail's line style (`chrome.tsx::NAV_ICONS`). */
  readonly icon: string;
  subject(item: I): string;
  note(item: I, answered: boolean): ReactNode;
  requestedBy(item: I): ReactNode;
}

/** The investigation that asked, on its own page, in its own workspace (`Links.run`'s shape). */
function investigationLink(runId: string, workspaceId: string | null): ReactNode {
  const path = runPagePath(runId);
  return <a href={workspaceId === null ? path : `${path}?workspace=${encodeURIComponent(workspaceId)}`}>Investigation</a>;
}

/** A title a person can read, or the kind's own name when it is missing or only spaces. */
export function titleOr(title: string | null | undefined, fallback: string): string {
  return title?.trim() ? title : fallback;
}

/**
 * The part of an ARN that names the resource: its resource field, less a leading `type:`
 * where the field has one. So a versioned Lambda reads `checkout:3` (#858), an S3 object
 * `bucket/key`, and a queue its name. What is not an ARN reads as it is.
 */
export function resourceNameOf(arn: string): string {
  const resource = arn.split(":").slice(5).join(":");
  // Only a leading `type:`, with no slash before it: a colon later in the field is the
  // name's own, an S3 key's timestamp or an ECS revision (#971).
  const name = resource.replace(/^[^/:]+:/, "");
  return name === "" ? arn : name;
}

export const KIND_ROWS: { readonly [K in InboxKind]: KindRow<RowOf<K>> } = {
  change_approval: {
    // A clipboard with a tick: a change waiting to be approved.
    icon: "M6 5h12v16H6z M9 3h6v4H9z M9 14l2 2 4-4",
    subject: (item) => titleOr(item.detail.title, "A proposed change"),
    note: (item, answered) =>
      item.detail.holder.kind === "org"
        ? `${item.detail.holder.name || "The partner"} ${answered ? "held" : "answers"} it, through its partner access to approve writes`
        : null,
    requestedBy: (item) => investigationLink(item.detail.run_id, item.workspace_id),
  },
  consent_request: {
    // An eye: a request to read the data itself.
    icon: "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
    subject: (item) => resourceNameOf(item.detail.resource_arn),
    note: (item) => item.detail.operations.join(", ") || null,
    requestedBy: (item) => investigationLink(item.detail.run_id, item.workspace_id),
  },
  capability_request: {
    // A key: what a partner asks to be let in to do.
    icon: "M8 11a4 4 0 1 0 0 8 4 4 0 0 0 0-8z M10.8 12.2L20 3 M17 6l2.5 2.5 M14.5 8.5l2 2",
    subject: (item) => CAPABILITY_TEXT[item.detail.capability as Capability]?.title ?? item.detail.capability,
    note: (item) => (item.detail.requested_by === null ? null : `Asked by ${item.detail.requested_by}`),
    requestedBy: (item) => item.detail.requester ?? "A partner",
  },
  held_alert: {
    // A bell: an alarm that would have started an investigation past today's limit (#849).
    icon: "M6 16v-5a6 6 0 0 1 12 0v5l2 2H4z M10 20a2 2 0 0 0 4 0",
    subject: (item) => item.detail.alarm,
    note: (item, answered) =>
      `${severityLabel(item.detail.severity)}${item.detail.occurrences > 1 ? `, fired ${item.detail.occurrences} times` : ""}. ${
        answered ? "Held" : item.detail.resolved ? "Resolved while waiting" : "Waiting"
      } past today's limit on automatic investigations`,
    requestedBy: (item) => item.detail.source ?? "An alert source",
  },
  invitation: {
    // An envelope: an invitation addressed to you.
    icon: "M3 6h18v12H3z M3 6l9 7 9-7",
    subject: (item) => `Join as ${item.detail.role}`,
    note: () => null,
    requestedBy: (item) => item.detail.invited_by ?? "Unknown",
  },
};

/** What each outcome reads as, by kind: the item's own words for its answer. */
const OUTCOME_TEXT: Record<InboxKind, Record<string, string>> = {
  invitation: { accepted: "Accepted" },
  consent_request: { granted: "Allowed", declined: "Declined", expired: "Expired unanswered" },
  capability_request: { granted: "Allowed", declined: "Declined" },
  change_approval: { approved: "Approved", declined: "Declined", expired: "Expired unanswered" },
  held_alert: { investigated: "Investigated", dismissed: "Dismissed" },
};

/** An answered item's outcome, in its own words. */
export function outcomeText(item: AnsweredItem): string {
  return OUTCOME_TEXT[item.kind][item.answer.outcome] ?? item.answer.outcome;
}

/** `2026-09-14 09:00 UTC`: one reading of the time for every viewer. */
export function whenOf(at: string): string {
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? at : `${date.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

/** The answer in a sentence's words: the outcome, who gave it and when, where the audit row says. */
export function answerSentence(item: AnsweredItem): string {
  const { by, at } = item.answer;
  return `${outcomeText(item)}${by === null ? "" : ` by ${by}`}${at === null ? "" : ` on ${whenOf(at)}`}`;
}

/** The row of any item, typed as the item it is handed. */
export const rowOf = (item: RowItem): KindRow<RowItem> => KIND_ROWS[item.kind] as KindRow<RowItem>;

/** The kind's icon, named for a screen reader by what one item of it is. */
export function KindIcon({ kind }: { kind: InboxKind }) {
  return (
    <svg
      className="inbox-kind"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={INBOX_KINDS[kind].one}
    >
      <title>{INBOX_KINDS[kind].one}</title>
      <path d={KIND_ROWS[kind].icon} />
    </svg>
  );
}
